/**
 * @module cercaliagl/interaction/Draw
 */
import MapboxglDraw from '@mapbox/mapbox-gl-draw';
// import FreehandMode from 'mapbox-gl-draw-freehand-mode';
import DrawRectangleMode from 'mapbox-gl-draw-rectangle-mode';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { SnapPolygonMode, SnapPointMode } from 'mapbox-gl-draw-snap-mode';
import FreehandMode from './FreehandDraw';
/**
 * @enum {string}
 */
var Mode = {
    POINT: 'draw_point',
    LINE: 'draw_line_string',
    POLYGON: 'draw_polygon',
    RECTANGLE: 'draw_rectangle',
    SIMPLE_SELECT: 'simple_select',
    SNAP_POINT: 'draw_snap_point',
    SNAP_POLYGON: 'draw_snap_polygon',
};
/**
 * @type {Object.<string, string>}
 * @api
 */
export var Type = {
    POINT: 'point',
    LINE: 'line',
    POLYGON: 'polygon',
    RECTANGLE: 'rectangle',
    SNAP_POINT: 'snap_point',
    SNAP_POLYGON: 'snap_polygon',
};
/**
 * @classdesc
 * Provides draw interaction. It is possible to draw different geometry types.
 * Geometry types available are:
 * <br/>
 * <ul>
 *   <li><b>- cercaliagl.interaction.Draw.Type.POINT</b></li>
 *   <li><b>- cercaliagl.interaction.Draw.Type.LINE</b></li>
 *   <li><b>- cercaliagl.interaction.Draw.Type.POLYGON</b></li>
 *   <li><b>- cercaliagl.interaction.Draw.Type.RECTANGLE</b></li>
 * </ul>
 * <br/>
 * This interaction can be accessed via map object and draw a feature:
 * <br/>
 * <br/>
 *
 *
 * ```
 * const map = new cercaliagl.Map({ target: 'map', controls: []});
 * const drawInteraction = map.getDrawInteraction();
 * drawInteraction.enable(cercaliagl.interaction.Draw.Type.POLYGON, function(data) {
 *   console.log('Geometry', data.geometry);
 * });
 * ```
 *
 * @api
 */
var Draw = /** @class */ (function () {
    /**
     * @constructor
     * @param {import("../Map").default} map
     */
    function Draw(map) {
        /**
         * @private
         * @type {import("../Map").default}
         */
        this.map_ = map;
        /**
         * @private
         * @type {import("maplibre-gl").Map}
         */
        this.mbMap_ = this.map_.getMapboxglMap();
        /**
         * @private
         * @type {boolean}
         */
        this.active_ = false;
        // gl-draw-line
        // gl-draw-polygon
        // gl-draw-polygon-stroke-active
        // gl-draw-polygon-and-line-vertex-halo-active
        // gl-draw-polygon-and-line-vertex-active
        /**
         * @private
         * @type {MapboxglDraw}
         */
        this.draw_ = new MapboxglDraw({
            modes: Object.assign(MapboxglDraw.modes, {
                draw_polygon: FreehandMode,
                draw_rectangle: DrawRectangleMode,
                draw_snap_polygon: SnapPolygonMode,
                draw_snap_point: SnapPointMode,
            }),
            snap: false,
            snapOptions: {
                snapPx: 15,
                snapToMidPoints: true,
            },
            userProperties: true,
        });
        /**
         * @private
         * @type {Function}
         */
        this.handleDrawEndBound_ = null;
        this.initialize_();
    }
    /**
     * @static
     */
    Draw.getModeByType = function (type) {
        if (type === Type.POINT) {
            return Mode.POINT;
        }
        if (type === Type.LINE) {
            return Mode.LINE;
        }
        if (type === Type.POLYGON) {
            return Mode.POLYGON;
        }
        if (type === Type.RECTANGLE) {
            return Mode.RECTANGLE;
        }
        if (type === Type.SNAP_POINT) {
            return Mode.SNAP_POINT;
        }
        if (type === Type.SNAP_POLYGON) {
            return Mode.SNAP_POLYGON;
        }
        return null;
    };
    /**
     * @private
     */
    Draw.prototype.initialize_ = function () {
        var _this = this;
        var drawOld = this.draw_.onAdd;
        this.active_ = false;
        this.draw_.onAdd = function (_map) {
            var element = drawOld(_map);
            element.hidden = true;
            return element;
        };
        this.mbMap_.addControl(this.draw_);
        setTimeout(function () { return _this.overrideStyles_(); }, 3000);
    };
    /**
     * @private
     */
    Draw.prototype.overrideStyles_ = function () {
        var _this = this;
        var color = '#000000';
        var circleRadius = 5;
        var style = this.mbMap_.getStyle();
        if (style) {
            var layers = this.mbMap_.getStyle().layers;
            layers.forEach(function (layer) {
                if (layer.id.indexOf('gl-draw') !== -1) {
                    _this.mbMap_.setPaintProperty(layer.id, "".concat(layer.type, "-color"), color);
                    _this.mbMap_.setFilter(layer.id, ['!=', ['get', 'user_show'], false]);
                    // this.mbMap_.setPaintProperty(layer.id, `${layer.type}-opacity`, ['case', ['boolean', ['get', 'user_show'], true], layer.paint[`${layer.type}-opacity`] ? layer.paint[`${layer.type}-opacity`] : 1, 0]);
                    if (layer.type === 'circle') {
                        _this.mbMap_.setPaintProperty(layer.id, 'circle-radius', layer.id.indexOf('midpoint') === -1 ? circleRadius : circleRadius - 1);
                    }
                }
            });
        }
    };
    /**
     * @private
     * @param {string} featureId FeatureId
     */
    Draw.prototype.hideFeature_ = function (featureId) {
        this.draw_.setFeatureProperty(featureId, 'show', false);
        this.draw_.add(this.draw_.get(featureId));
        this.draw_.changeMode('simple_select', { featureId: this.draw_.get(featureId).id });
    };
    /**
     * @private
     * @param {string} featureId FeatureId
     */
    Draw.prototype.showFeature_ = function (featureId) {
        this.draw_.setFeatureProperty(featureId, 'show', true);
        this.draw_.add(this.draw_.get(featureId));
    };
    /**
     * Enable interaction gived a geometry type. Geometry types available are: `point`, `line`, `polygon`, `rectangle`.<br/>
     * You can access them via:
     * <ul>
     *   <li><b>cercaliagl.interaction.Draw.Type.POINT</b></li>
     *   <li><b>cercaliagl.interaction.Draw.Type.LINE</b></li>
     *   <li><b>cercaliagl.interaction.Draw.Type.POLYGON</b></li>
     *   <li><b>cercaliagl.interaction.Draw.Type.RECTANGLE</b></li>
     * </ul>
     * <br/>
     * Optionally you can specify a callback function. Callback function received feature as parameter.
     *
     * @api
     * @param {string} type Type
     * @param {Function} [callbackFn]
     */
    Draw.prototype.enable = function (type, callbackFn) {
        this.active_ = true;
        this.draw_.changeMode(Draw.getModeByType(type));
        this.handleDrawEndBound_ = this.handleDrawEnd_.bind(this, callbackFn);
        this.mbMap_.once('draw.create', this.handleDrawEndBound_);
    };
    /**
     * @private
     * @param {Event} event
     * @param {Function} [callbackFn]
     */
    Draw.prototype.handleDrawEnd_ = function (callbackFn, event) {
        var _this = this;
        var features = event.features;
        if (features.length > 0) {
            var feature = features[0];
            var featureId_1 = feature.id;
            if (callbackFn) {
                callbackFn(feature);
            }
            else {
                console.warn('No callback function defined for drawEnd'); // eslint-disable-line no-console
            }
            setTimeout(function () { return _this.hideFeature_(featureId_1); }, 2500); // Amaguem l'original
            // setTimeout(() => this.draw_.delete([featureId]), 2500); // Eliminem l'original
        }
        this.active_ = false;
    };
    /**
     * Select feature
     * @param {import("../Feature").default} feature Feature
     */
    Draw.prototype.select = function (feature) {
        var _this = this;
        var geojson = feature.getGeoJSON();
        geojson.id = feature.getId();
        this.draw_.add(geojson);
        this.draw_.changeMode('direct_select', { featureId: geojson.id });
        if (feature.onModifyStart) {
            feature.onModifyStart(feature);
        }
        setTimeout(function () { return _this.mbMap_.once('click', function () { return _this.unselect(feature); }); }, 100);
    };
    /**
     * @param {import("../Feature").default} feature Feature
     * @param {GeoJSON} feature Feature
     */
    Draw.prototype.unselect = function (feature) {
        var featureId = feature.getId();
        var geojson = this.draw_.get(featureId);
        feature.setVisible(true);
        feature.setGeometry(geojson.geometry);
        if (feature.onModifyEnd) {
            feature.onModifyEnd(feature);
        }
        this.hideFeature_(featureId);
        // this.draw_.delete(featureId);
    };
    /**
     * Disable interaction.
     * @api
     */
    Draw.prototype.disable = function () {
        this.mbMap_.off('draw.create', this.handleDrawEndBound_);
        this.draw_.changeMode(Mode.SIMPLE_SELECT);
        this.active_ = false;
    };
    /**
     * @return {import("../Map").default} map
     */
    Draw.prototype.getMap = function () {
        return this.map_;
    };
    /**
     * Return `true` if interaction is active. Else `false`.
     * @api
     * @return {boolean}
     */
    Draw.prototype.isActive = function () {
        return this.active_;
    };
    /**
     * Enables snap.
     * @api
     */
    Draw.prototype.enableSnap = function () {
        this.draw_.options.snap = true;
    };
    /**
     * Disables snap.
     * @api
     */
    Draw.prototype.disableSnap = function () {
        this.draw_.options.snap = false;
    };
    return Draw;
}());
export default Draw;
//# sourceMappingURL=Draw.js.map